import React, { useMemo } from "react";
import _ from "lodash";

import CustomCoverPage from "../custom-general/custom-cover-page";
import CustomInfoSheet from "../custom-general/custom-info-sheet";
import CustomPDFExport from "../../blocks/pdf-export";
import ExecutiveSummary from "../custom-visual-inspection/executive-summary";
import OverviewSheet from "../custom-general/overview-sheet";
import MaintenancePracticeReport from "./maintenance-practice";
import PCNLSummary from "./pcnl-summary";
import PCNLAnomalyRecommendation from "../custom-visual-inspection/anomaly-recommendation/pcnlAnomalyRecommendation";
import PCNLItemReport from "./pcnl-item/report";
import PCNLRiskClassification from "./risk-classification";

import withPDF from "../../hoc/withPDF";
import useCoverSheetOverview from "../../../hooks/useCoverSheetOverview";

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface Props {
	maintenancePractice: models.PCNLMaintenancePractice;
    coverData: models.CustomFVIData;
    executiveSummary: string;
    projectDetails: models.Project;
    reportTitle: string;
    pcnlItems: models.PCNLItemModel[];
}

const orderPCNLAnomalyItems = (anomalyItems: { riskFactor: string }[]) => {
	const orderedByRiskFactor = _.orderBy(anomalyItems, (anomaly) => {
		const riskFactorClass = utils.getRiskFactorEquivaluent(anomaly.riskFactor);
		return riskFactorClass ? riskFactorClass.id : anomaly.riskFactor;
	}, 'asc');

	return utils.reArrangeAnomalies(orderedByRiskFactor);
};

const orderPCNLRiskItems = (riskItems: { riskRating: string; }[]) => {
	return _.orderBy(riskItems, (riskItem) => {
		const riskFactorClass = utils.getRiskFactorEquivaluent(riskItem.riskRating);
		return riskFactorClass ? riskFactorClass.id : riskItem.riskRating;
	}, 'asc');
};

const orderPCNLItems = (pcnlItems: { maxRating: string; }[]) => {
	return _.orderBy(pcnlItems, (pcnlItem) => {
		const riskFactorClass = utils.getRiskFactorEquivaluent(pcnlItem.maxRating);
		return riskFactorClass ? riskFactorClass.id : pcnlItem.maxRating;
	}, 'asc');
};

const CustomPCNLReport: React.FC<Props> = ({ executiveSummary, maintenancePractice, projectDetails, coverData, pcnlItems, reportTitle }) => {
    const { inspectionTypeName } = projectDetails;

    const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. PCNL Work Procedure.',
			value: coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. Arridex Procedure.',
			value: coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [coverData.coverData]);

	const orderedPCNLItems: any = useMemo(() => {
		return pcnlItems.map((pcnlItem) => ({ 
			...pcnlItem, 
			maxRating: utils.getMaxRatingFromInspectedItem(pcnlItem), 
			anomalyItems: orderPCNLAnomalyItems(pcnlItem.anomalyItems), 
			riskAssessment: orderPCNLRiskItems(pcnlItem.riskAssessment)  
		}));
	}, [pcnlItems]);

	const orderedPCNLItemsForAnomalyRecommendation = useMemo(() => {
		return utils.reArrangeInspectionItemAnomalies(orderPCNLItems(orderedPCNLItems) as unknown as models.PCNLItemModel[]);
	}, [orderedPCNLItems]);

	const overview = useCoverSheetOverview();
	const inspectionTypeLabel = utils.getExtractedInspectionName(inspectionTypeName);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={inspectionTypeLabel} />

			<CustomInfoSheet
				projectDetails={projectDetails}
				workOrderNumber={coverData.workOrderNumber}
				reportTitle={reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={projectDetails}
			/>

			<ExecutiveSummary summary={executiveSummary} />

			<MaintenancePracticeReport 
				currentPractice={maintenancePractice.currentPractice} 
				recommendedPractice={maintenancePractice.recommendedPractice} 
			/>
			
			<PCNLRiskClassification />

            <PCNLSummary pcnlItems={orderedPCNLItems} />

			<CustomPDFExport>
				{(params) => (
					<PCNLAnomalyRecommendation
						projectDetails={projectDetails} 
						items={orderedPCNLItemsForAnomalyRecommendation as models.PCNLItemModel[]}
						exportParams={params}
					/>
				)}
			</CustomPDFExport>
			
            <PCNLItemReport pcnlItems={orderedPCNLItems} />
        </React.Fragment>
     );
};
 
export default withPDF(CustomPCNLReport);